// 縦書き時の記号類のオフセット(文字送り単位)をフォントごとに定義する。
//
// 値は「ブラウザの writing-mode: vertical-rl(フォント自身の vert 字形)で描いた記号の位置」と
// 「横書き字形を回転・反転して描いたときの位置」の差を実測したもの(フォントの縦書き字形の位置に合わせる)。
// 計測スクリプトは documents/vertical-glyph-adjust.md を参照。

export type GlyphOffset = [number, number]; // [ax, ay] charSkip 単位、右・下が正

// 全角幅の枠に対する字形の寄せ方
export type GlyphAlign = "center" | "left" | "right";

export type VerticalGlyphAdjust = {
  punctuation: GlyphOffset;        // 、。
  doubleQuoteOpen: GlyphOffset;    // “ (〝 を左右反転して描く)
  doubleQuoteClose: GlyphOffset;   // ” (〟 を左右反転して描く)
  smallKana: GlyphOffset;          // ぁぃぅ…ッャュョヵヶ
  cornerBracketOpen: GlyphOffset;  // 「『
  cornerBracketClose: GlyphOffset; // 」』
  colon: GlyphOffset;              // ：；
  bracketOpen: GlyphOffset;        // ＜（【〔≪｛
  bracketClose: GlyphOffset;       // ＞）】〕≫｝
  prolongedSound: GlyphOffset;     // ー
  ellipsis: GlyphOffset;           // …
  dash: GlyphOffset;               // ―〰
  wave: GlyphOffset;               // ～〜
};

// テーブルに無いフォント(ユーザーのローカルフォント等)で使う既定値。vert字形を持つフォント群の典型値
export const defaultVerticalGlyphAdjust: VerticalGlyphAdjust = {
  punctuation: [0.6, -0.6],
  doubleQuoteOpen: [0.55, 0.55],
  doubleQuoteClose: [-0.55, -0.55],
  smallKana: [0.1, -0.1],
  cornerBracketOpen: [0.12, 0.12],
  cornerBracketClose: [0.12, 0.12],
  colon: [0.16, 0.16],
  bracketOpen: [0.12, 0.12],
  bracketClose: [0.12, 0.12],
  prolongedSound: [-0.12, 0.12],
  ellipsis: [-0.15, 0.11],
  dash: [-0.15, 0.11],
  wave: [-0.12, 0.12],
};
// フォントごとの値(fontWeightMap のキーと同じファミリ名)
export const verticalGlyphAdjustTable: Record<string, VerticalGlyphAdjust> = {
  "源暎アンチック": {
    punctuation: [0.63, -0.64],
    doubleQuoteOpen: [0.54, 0.64],
    doubleQuoteClose: [-0.54, -0.64],
    smallKana: [0.14, -0.14],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.13, 0.12],
    colon: [0.20, 0.19],
    bracketOpen: [0.12, 0.12],
    bracketClose: [0.12, 0.12],
    prolongedSound: [-0.17, 0.12],
    ellipsis: [-0.11, 0.11],
    dash: [-0.12, 0.11],
    wave: [-0.13, 0.12],
  },
  "源暎エムゴ": {
    punctuation: [0.36, -0.53],
    doubleQuoteOpen: [0.23, 0.56],
    doubleQuoteClose: [-0.24, -0.56],
    smallKana: [0.13, -0.08],
    cornerBracketOpen: [0.16, 0.11],
    cornerBracketClose: [0.08, 0.13],
    colon: [0.14, 0.15],
    bracketOpen: [0.12, 0.12],
    bracketClose: [0.12, 0.12],
    prolongedSound: [-0.12, 0.12],
    ellipsis: [-0.12, 0.12],
    dash: [-0.11, 0.12],
    wave: [-0.12, 0.12],
  },
  "源暎ぽっぷる": {
    punctuation: [0.36, -0.55],
    doubleQuoteOpen: [0.23, 0.54],
    doubleQuoteClose: [-0.24, -0.58],
    smallKana: [0.10, -0.10],
    cornerBracketOpen: [0.05, 0.10],
    cornerBracketClose: [0.19, 0.10],
    colon: [0.16, 0.17],
    bracketOpen: [0.12, 0.09],
    bracketClose: [0.12, 0.11],
    prolongedSound: [-0.12, 0.12],
    ellipsis: [-0.13, 0.10],
    dash: [-0.11, 0.11],
    wave: [-0.12, 0.12],
  },
  "源暎ラテゴ": {
    punctuation: [0.61, -0.59],
    doubleQuoteOpen: [0.57, 0.58],
    doubleQuoteClose: [-0.57, -0.59],
    smallKana: [0.11, -0.10],
    cornerBracketOpen: [0.13, 0.12],
    cornerBracketClose: [0.11, 0.11],
    colon: [0.16, 0.17],
    bracketOpen: [0.12, 0.11],
    bracketClose: [0.12, 0.11],
    prolongedSound: [-0.13, 0.10],
    ellipsis: [-0.11, 0.11],
    dash: [-0.12, 0.11],
    wave: [-0.12, 0.12],
  },
  "源暎ラテミン": {
    punctuation: [0.61, -0.58],
    doubleQuoteOpen: [0.57, 0.59],
    doubleQuoteClose: [-0.57, -0.58],
    smallKana: [0.11, -0.09],
    cornerBracketOpen: [0.13, 0.13],
    cornerBracketClose: [0.11, 0.12],
    colon: [0.16, 0.17],
    bracketOpen: [0.12, 0.12],
    bracketClose: [0.12, 0.12],
    prolongedSound: [-0.13, 0.11],
    ellipsis: [-0.11, 0.12],
    dash: [-0.12, 0.12],
    wave: [-0.12, 0.12],
  },
  "源暎きわみゴ": {
    punctuation: [0.50, -0.49],
    doubleQuoteOpen: [0.23, 0.54],
    doubleQuoteClose: [-0.24, -0.59],
    smallKana: [0.11, -0.09],
    cornerBracketOpen: [0.16, 0.09],
    cornerBracketClose: [0.08, 0.11],
    colon: [0.14, 0.15],
    bracketOpen: [0.12, 0.11],
    bracketClose: [0.12, 0.09],
    prolongedSound: [-0.12, 0.10],
    ellipsis: [-0.11, 0.12],
    dash: [-0.12, 0.12],
    wave: [-0.12, 0.12],
  },
  "ふい字": {
    punctuation: [0.47, -0.48],
    doubleQuoteOpen: [0.34, 0.48],
    doubleQuoteClose: [-0.41, -0.47],
    smallKana: [0.15, -0.19],
    cornerBracketOpen: [0.21, 0.20],
    cornerBracketClose: [0.09, 0.07],
    colon: [0.23, 0.25],
    bracketOpen: [0.17, 0.18],
    bracketClose: [0.17, 0.07],
    prolongedSound: [-0.23, 0.19],
    ellipsis: [-0.36, 0.14],
    dash: [-0.35, 0.14],
    wave: [-0.28, 0.22],
  },
  "まきばフォント": {
    punctuation: [0.52, -0.49],
    doubleQuoteOpen: [0.46, 0.52],
    doubleQuoteClose: [-0.43, -0.57],
    smallKana: [0.18, -0.06],
    cornerBracketOpen: [0.09, 0.22],
    cornerBracketClose: [0.26, 0.07],
    colon: [0.20, 0.17],
    bracketOpen: [0.16, 0.14],
    bracketClose: [0.15, 0.16],
    prolongedSound: [-0.20, 0.18],
    ellipsis: [-0.15, 0.14],
    dash: [-0.10, 0.14],
    wave: [-0.19, 0.17],
  },
  "BIZ UDGothic": {
    punctuation: [0.61, -0.60],
    doubleQuoteOpen: [0.57, 0.55],
    doubleQuoteClose: [-0.57, -0.54],
    smallKana: [0.09, -0.09],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.12, 0.12],
    colon: [0.15, 0.17],
    bracketOpen: [0.12, 0.12],
    bracketClose: [0.12, 0.12],
    prolongedSound: [-0.10, 0.12],
    ellipsis: [-0.12, 0.12],
    dash: [-0.12, 0.12],
    wave: [-0.12, 0.12],
  },
  "BIZ UDMincho": {
    punctuation: [0.65, -0.65],
    doubleQuoteOpen: [0.55, 0.56],
    doubleQuoteClose: [-0.56, -0.55],
    smallKana: [0.12, -0.12],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.12, 0.12],
    colon: [0.16, 0.17],
    bracketOpen: [0.12, 0.13],
    bracketClose: [0.12, 0.12],
    prolongedSound: [-0.12, 0.13],
    ellipsis: [-0.12, 0.12],
    dash: [-0.12, 0.12],
    wave: [-0.12, 0.12],
  },
  "BIZ UDPGothic": {
    punctuation: [0.41, -0.60],
    doubleQuoteOpen: [0.57, 0.55],
    doubleQuoteClose: [-0.57, -0.54],
    smallKana: [0.09, -0.15],
    cornerBracketOpen: [0.11, 0.13],
    cornerBracketClose: [0.13, 0.11],
    colon: [0.15, 0.17],
    bracketOpen: [0.12, 0.12],
    bracketClose: [0.12, 0.11],
    prolongedSound: [-0.10, 0.10],
    ellipsis: [-0.12, 0.12],
    dash: [-0.12, 0.12],
    wave: [-0.12, 0.12],
  },
  "BIZ UDPMincho": {
    punctuation: [0.45, -0.65],
    doubleQuoteOpen: [0.55, 0.56],
    doubleQuoteClose: [-0.56, -0.55],
    smallKana: [0.12, -0.20],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.14, 0.12],
    colon: [0.16, 0.16],
    bracketOpen: [0.13, 0.12],
    bracketClose: [0.13, 0.12],
    prolongedSound: [-0.12, 0.10],
    ellipsis: [-0.12, 0.12],
    dash: [-0.12, 0.12],
    wave: [-0.12, 0.12],
  },
  "Dela Gothic One": {
    punctuation: [0.30, -0.62],
    doubleQuoteOpen: [0.31, 0.54],
    doubleQuoteClose: [-0.36, -0.57],
    smallKana: [0.10, -0.08],
    cornerBracketOpen: [0.01, 0.10],
    cornerBracketClose: [0.37, 0.10],
    colon: [0.18, 0.18],
    bracketOpen: [0.12, 0.10],
    bracketClose: [0.12, 0.11],
    prolongedSound: [-0.12, 0.11],
    ellipsis: [-0.47, 0.02],
    dash: [-0.18, 0.10],
    wave: [-0.15, 0.10],
  },
  "DotGothic16": {
    punctuation: [0.62, -0.59],
    doubleQuoteOpen: [0.34, 0.53],
    doubleQuoteClose: [-0.34, -0.52],
    smallKana: [0.06, -0.14],
    cornerBracketOpen: [0.07, 0.13],
    cornerBracketClose: [0.12, 0.06],
    colon: [0.15, 0.16],
    bracketOpen: [0.08, 0.12],
    bracketClose: [0.08, 0.06],
    prolongedSound: [-0.09, 0.09],
    ellipsis: [-0.20, 0.09],
    dash: [-0.12, 0.09],
    wave: [-0.11, 0.10],
  },
  "Hachi Maru Pop": {
    punctuation: [0.62, -0.64],
    doubleQuoteOpen: [0.49, 0.62],
    doubleQuoteClose: [-0.48, -0.60],
    smallKana: [0.10, -0.12],
    cornerBracketOpen: [0.21, 0.20],
    cornerBracketClose: [0.15, 0.18],
    colon: [0.23, 0.23],
    bracketOpen: [0.16, 0.17],
    bracketClose: [0.16, 0.19],
    prolongedSound: [-0.21, 0.20],
    ellipsis: [-0.30, 0.12],
    dash: [-0.26, 0.15],
    wave: [-0.23, 0.24],
  },
  "Hina Mincho": {
    punctuation: [0.55, -0.55],
    doubleQuoteOpen: [0.52, 0.60],
    doubleQuoteClose: [-0.51, -0.62],
    smallKana: [0.15, -0.12],
    cornerBracketOpen: [0.08, 0.01],
    cornerBracketClose: [0.15, 0.22],
    colon: [0.15, 0.12],
    bracketOpen: [0.11, 0.07],
    bracketClose: [0.11, 0.14],
    prolongedSound: [-0.14, 0.14],
    ellipsis: [-0.52, 0.12],
    dash: [-0.25, 0.11],
    wave: [-0.14, 0.11],
  },
  "IBM Plex Sans JP": {
    punctuation: [0.60, -0.63],
    doubleQuoteOpen: [0.55, 0.56],
    doubleQuoteClose: [-0.55, -0.56],
    smallKana: [0.11, -0.11],
    cornerBracketOpen: [0.12, 0.11],
    cornerBracketClose: [0.12, 0.11],
    colon: [0.15, 0.16],
    bracketOpen: [0.14, 0.11],
    bracketClose: [0.14, 0.11],
    prolongedSound: [-0.12, 0.11],
    ellipsis: [-0.04, 0.11],
    dash: [-0.08, 0.11],
    wave: [-0.12, 0.11],
  },
  "Kaisei Decol": {
    punctuation: [0.61, -0.66],
    doubleQuoteOpen: [0.48, 0.52],
    doubleQuoteClose: [-0.49, -0.54],
    smallKana: [0.11, -0.15],
    cornerBracketOpen: [0.10, 0.14],
    cornerBracketClose: [0.13, 0.09],
    colon: [0.18, 0.18],
    bracketOpen: [0.11, 0.13],
    bracketClose: [0.11, 0.10],
    prolongedSound: [-0.13, 0.10],
    ellipsis: [-0.18, 0.11],
    dash: [-0.19, 0.14],
    wave: [-0.15, 0.12],
  },
  "Kaisei HarunoUmi": {
    punctuation: [0.60, -0.66],
    doubleQuoteOpen: [0.48, 0.52],
    doubleQuoteClose: [-0.49, -0.54],
    smallKana: [0.09, -0.15],
    cornerBracketOpen: [0.10, 0.14],
    cornerBracketClose: [0.13, 0.09],
    colon: [0.18, 0.18],
    bracketOpen: [0.11, 0.13],
    bracketClose: [0.11, 0.10],
    prolongedSound: [-0.18, 0.13],
    ellipsis: [-0.18, 0.11],
    dash: [-0.19, 0.14],
    wave: [-0.15, 0.12],
  },
  "Kaisei Opti": {
    punctuation: [0.61, -0.66],
    doubleQuoteOpen: [0.48, 0.52],
    doubleQuoteClose: [-0.49, -0.54],
    smallKana: [0.10, -0.12],
    cornerBracketOpen: [0.10, 0.14],
    cornerBracketClose: [0.13, 0.09],
    colon: [0.18, 0.18],
    bracketOpen: [0.11, 0.13],
    bracketClose: [0.11, 0.10],
    prolongedSound: [-0.12, 0.11],
    ellipsis: [-0.18, 0.11],
    dash: [-0.19, 0.14],
    wave: [-0.15, 0.12],
  },
  "Kaisei Tokumin": {
    punctuation: [0.60, -0.63],
    doubleQuoteOpen: [0.48, 0.52],
    doubleQuoteClose: [-0.49, -0.54],
    smallKana: [0.09, -0.13],
    cornerBracketOpen: [0.10, 0.14],
    cornerBracketClose: [0.13, 0.09],
    colon: [0.18, 0.18],
    bracketOpen: [0.11, 0.13],
    bracketClose: [0.11, 0.10],
    prolongedSound: [-0.15, 0.12],
    ellipsis: [-0.18, 0.11],
    dash: [-0.19, 0.14],
    wave: [-0.15, 0.12],
  },
  "Kiwi Maru": {
    punctuation: [0.68, -0.70],
    doubleQuoteOpen: [0.72, 0.71],
    doubleQuoteClose: [-0.72, -0.72],
    smallKana: [0.11, -0.11],
    cornerBracketOpen: [0.11, 0.10],
    cornerBracketClose: [0.11, 0.11],
    colon: [0.15, 0.14],
    bracketOpen: [0.10, 0.11],
    bracketClose: [0.10, 0.11],
    prolongedSound: [-0.12, 0.10],
    ellipsis: [-0.17, 0.10],
    dash: [-0.14, 0.10],
    wave: [-0.12, 0.12],
  },
  "Klee One": {
    punctuation: [0.60, -0.63],
    doubleQuoteOpen: [0.57, 0.60],
    doubleQuoteClose: [-0.55, -0.58],
    smallKana: [0.14, -0.10],
    cornerBracketOpen: [0.14, 0.14],
    cornerBracketClose: [0.14, 0.14],
    colon: [0.16, 0.19],
    bracketOpen: [0.12, 0.13],
    bracketClose: [0.13, 0.13],
    prolongedSound: [-0.16, 0.16],
    ellipsis: [-0.17, 0.12],
    dash: [-0.14, 0.14],
    wave: [-0.12, 0.12],
  },
  "Kosugi": {
    punctuation: [0.63, -0.64],
    doubleQuoteOpen: [0.55, 0.55],
    doubleQuoteClose: [-0.56, -0.55],
    smallKana: [0.11, -0.11],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.12, 0.11],
    colon: [0.15, 0.14],
    bracketOpen: [0.12, 0.11],
    bracketClose: [0.12, 0.11],
    prolongedSound: [-0.12, 0.11],
    ellipsis: [-0.11, 0.11],
    dash: [-0.12, 0.11],
    wave: [-0.12, 0.12],
  },
  "Kosugi Maru": {
    punctuation: [0.64, -0.63],
    doubleQuoteOpen: [0.57, 0.53],
    doubleQuoteClose: [-0.56, -0.56],
    smallKana: [0.11, -0.11],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.12, 0.12],
    colon: [0.15, 0.15],
    bracketOpen: [0.12, 0.12],
    bracketClose: [0.12, 0.12],
    prolongedSound: [-0.11, 0.12],
    ellipsis: [-0.12, 0.12],
    dash: [-0.12, 0.11],
    wave: [-0.11, 0.12],
  },
  "M PLUS 1": {
    punctuation: [0.58, -0.58],
    doubleQuoteOpen: [0.49, 0.53],
    doubleQuoteClose: [-0.51, -0.56],
    smallKana: [0.08, -0.09],
    cornerBracketOpen: [0.14, 0.14],
    cornerBracketClose: [0.14, 0.14],
    colon: [0.22, 0.24],
    bracketOpen: [0.12, 0.17],
    bracketClose: [0.12, 0.07],
    prolongedSound: [-0.14, 0.13],
    ellipsis: [-0.52, 0.05],
    dash: [-0.18, 0.12],
    wave: [-0.17, 0.13],
  },
  "M PLUS 1 Code": {
    punctuation: [0.58, -0.60],
    doubleQuoteOpen: [0.32, 0.64],
    doubleQuoteClose: [-0.57, -0.67],
    smallKana: [0.10, -0.08],
    cornerBracketOpen: [0.15, 0.16],
    cornerBracketClose: [0.12, 0.09],
    colon: [0.26, 0.12],
    bracketOpen: [0.15, 0.24],
    bracketClose: [0.15, 0.00],
    prolongedSound: [-0.15, 0.13],
    ellipsis: [-0.51, 0.11],
    dash: [-0.24, 0.12],
    wave: [-0.13, 0.13],
  },
  "M PLUS 1p": {
    punctuation: [0.59, -0.58],
    doubleQuoteOpen: [0.48, 0.71],
    doubleQuoteClose: [-0.48, -0.72],
    smallKana: [0.08, -0.08],
    cornerBracketOpen: [0.14, 0.13],
    cornerBracketClose: [0.14, 0.14],
    colon: [0.15, 0.17],
    bracketOpen: [0.13, 0.13],
    bracketClose: [0.13, 0.13],
    prolongedSound: [-0.14, 0.13],
    ellipsis: [-0.16, 0.13],
    dash: [-0.15, 0.13],
    wave: [-0.14, 0.13],
  },
  "M PLUS 2": {
    punctuation: [0.58, -0.58],
    doubleQuoteOpen: [0.49, 0.53],
    doubleQuoteClose: [-0.51, -0.56],
    smallKana: [0.08, -0.08],
    cornerBracketOpen: [0.14, 0.14],
    cornerBracketClose: [0.14, 0.14],
    colon: [0.21, 0.24],
    bracketOpen: [0.12, 0.17],
    bracketClose: [0.12, 0.07],
    prolongedSound: [-0.14, 0.13],
    ellipsis: [-0.52, 0.05],
    dash: [-0.18, 0.12],
    wave: [-0.17, 0.13],
  },
  "M PLUS Rounded 1c": {
    punctuation: [0.60, -0.58],
    doubleQuoteOpen: [0.49, 0.71],
    doubleQuoteClose: [-0.47, -0.72],
    smallKana: [0.08, -0.08],
    cornerBracketOpen: [0.14, 0.13],
    cornerBracketClose: [0.14, 0.14],
    colon: [0.15, 0.17],
    bracketOpen: [0.14, 0.13],
    bracketClose: [0.14, 0.14],
    prolongedSound: [-0.14, 0.13],
    ellipsis: [-0.16, 0.13],
    dash: [-0.15, 0.13],
    wave: [-0.14, 0.13],
  },
  "Mochiy Pop One": {
    punctuation: [0.52, -0.54],
    doubleQuoteOpen: [0.46, 0.56],
    doubleQuoteClose: [-0.47, -0.54],
    smallKana: [0.10, -0.09],
    cornerBracketOpen: [0.11, 0.12],
    cornerBracketClose: [0.12, 0.12],
    colon: [0.13, 0.15],
    bracketOpen: [0.10, 0.12],
    bracketClose: [0.11, 0.12],
    prolongedSound: [-0.13, 0.13],
    ellipsis: [-0.17, 0.12],
    dash: [-0.15, 0.12],
    wave: [-0.11, 0.11],
  },
  "Mochiy Pop P One": {
    punctuation: [0.26, -0.54],
    doubleQuoteOpen: [0.24, 0.56],
    doubleQuoteClose: [-0.25, -0.56],
    smallKana: [0.09, -0.20],
    cornerBracketOpen: [0.01, 0.10],
    cornerBracketClose: [0.23, 0.14],
    colon: [0.13, 0.15],
    bracketOpen: [0.11, 0.10],
    bracketClose: [0.11, 0.14],
    prolongedSound: [-0.13, 0.13],
    ellipsis: [-0.17, 0.12],
    dash: [-0.15, 0.12],
    wave: [-0.11, 0.11],
  },
  "New Tegomin": {
    punctuation: [0.58, -0.61],
    doubleQuoteOpen: [0.57, 0.66],
    doubleQuoteClose: [-0.35, -0.65],
    smallKana: [0.09, -0.11],
    cornerBracketOpen: [0.19, -0.04],
    cornerBracketClose: [0.21, 0.32],
    colon: [0.31, 0.32],
    bracketOpen: [0.15, 0.03],
    bracketClose: [0.14, 0.24],
    prolongedSound: [-0.15, 0.19],
    ellipsis: [-0.26, 0.12],
    dash: [-0.19, 0.13],
    wave: [-0.12, 0.15],
  },
  "Noto Sans JP": {
    punctuation: [0.61, -0.58],
    doubleQuoteOpen: [0.58, 0.58],
    doubleQuoteClose: [-0.58, -0.60],
    smallKana: [0.12, -0.11],
    cornerBracketOpen: [0.11, 0.12],
    cornerBracketClose: [0.13, 0.11],
    colon: [0.16, 0.16],
    bracketOpen: [0.11, 0.11],
    bracketClose: [0.11, 0.12],
    prolongedSound: [-0.12, 0.11],
    ellipsis: [-0.17, 0.11],
    dash: [-0.15, 0.12],
    wave: [-0.12, 0.11],
  },
  "Noto Serif JP": {
    punctuation: [0.61, -0.63],
    doubleQuoteOpen: [0.58, 0.59],
    doubleQuoteClose: [-0.58, -0.59],
    smallKana: [0.15, -0.14],
    cornerBracketOpen: [0.11, 0.12],
    cornerBracketClose: [0.13, 0.12],
    colon: [0.18, 0.17],
    bracketOpen: [0.11, 0.12],
    bracketClose: [0.11, 0.10],
    prolongedSound: [-0.19, 0.14],
    ellipsis: [-0.18, 0.11],
    dash: [-0.13, 0.11],
    wave: [-0.12, 0.11],
  },
  "Potta One": {
    punctuation: [0.55, -0.59],
    doubleQuoteOpen: [0.50, 0.49],
    doubleQuoteClose: [-0.50, -0.52],
    smallKana: [0.14, -0.11],
    cornerBracketOpen: [0.11, 0.11],
    cornerBracketClose: [0.12, 0.11],
    colon: [0.15, 0.17],
    bracketOpen: [0.11, 0.12],
    bracketClose: [0.10, 0.11],
    prolongedSound: [-0.12, 0.12],
    ellipsis: [-0.49, 0.06],
    dash: [-0.15, 0.11],
    wave: [-0.15, 0.12],
  },
  "Rampart One": {
    punctuation: [0.51, -0.56],
    doubleQuoteOpen: [0.51, 0.49],
    doubleQuoteClose: [-0.53, -0.53],
    smallKana: [0.09, -0.09],
    cornerBracketOpen: [0.16, 0.11],
    cornerBracketClose: [0.12, 0.16],
    colon: [0.18, 0.19],
    bracketOpen: [0.12, 0.13],
    bracketClose: [0.13, 0.13],
    prolongedSound: [-0.15, 0.13],
    ellipsis: [-0.23, 0.11],
    dash: [-0.18, 0.11],
    wave: [-0.17, 0.11],
  },
  "Reggae One": {
    punctuation: [0.58, -0.63],
    doubleQuoteOpen: [0.54, 0.54],
    doubleQuoteClose: [-0.54, -0.56],
    smallKana: [0.11, -0.10],
    cornerBracketOpen: [0.11, 0.12],
    cornerBracketClose: [0.12, 0.12],
    colon: [0.17, 0.14],
    bracketOpen: [0.11, 0.11],
    bracketClose: [0.11, 0.11],
    prolongedSound: [-0.13, 0.12],
    ellipsis: [-0.22, 0.11],
    dash: [-0.15, 0.11],
    wave: [-0.13, 0.12],
  },
  "RocknRoll One": {
    punctuation: [0.57, -0.59],
    doubleQuoteOpen: [0.55, 0.54],
    doubleQuoteClose: [-0.54, -0.57],
    smallKana: [0.09, -0.09],
    cornerBracketOpen: [0.14, 0.11],
    cornerBracketClose: [0.12, 0.13],
    colon: [0.17, 0.14],
    bracketOpen: [0.12, 0.12],
    bracketClose: [0.12, 0.11],
    prolongedSound: [-0.12, 0.12],
    ellipsis: [-0.18, 0.11],
    dash: [-0.15, 0.11],
    wave: [-0.12, 0.11],
  },
  "Sawarabi Gothic": {
    punctuation: [0.57, -0.61],
    doubleQuoteOpen: [0.50, 0.59],
    doubleQuoteClose: [-0.51, -0.66],
    smallKana: [0.12, -0.09],
    cornerBracketOpen: [0.12, 0.16],
    cornerBracketClose: [0.16, 0.08],
    colon: [0.24, 0.23],
    bracketOpen: [0.16, 0.07],
    bracketClose: [0.16, 0.15],
    prolongedSound: [-0.12, 0.13],
    ellipsis: [-0.50, -0.11],
    dash: [-0.35, -0.11],
    wave: [-0.12, 0.12],
  },
  "Shippori Antique": {
    punctuation: [0.58, -0.63],
    doubleQuoteOpen: [0.52, 0.58],
    doubleQuoteClose: [-0.53, -0.57],
    smallKana: [0.10, -0.12],
    cornerBracketOpen: [0.11, 0.12],
    cornerBracketClose: [0.11, 0.12],
    colon: [0.14, 0.15],
    bracketOpen: [0.10, 0.11],
    bracketClose: [0.10, 0.11],
    prolongedSound: [-0.16, 0.11],
    ellipsis: [-0.17, 0.11],
    dash: [-0.15, 0.11],
    wave: [-0.13, 0.12],
  },
  "Shippori Antique B1": {
    punctuation: [0.58, -0.63],
    doubleQuoteOpen: [0.51, 0.58],
    doubleQuoteClose: [-0.52, -0.57],
    smallKana: [0.10, -0.12],
    cornerBracketOpen: [0.11, 0.12],
    cornerBracketClose: [0.11, 0.12],
    colon: [0.14, 0.15],
    bracketOpen: [0.10, 0.11],
    bracketClose: [0.10, 0.11],
    prolongedSound: [-0.16, 0.11],
    ellipsis: [-0.17, 0.11],
    dash: [-0.15, 0.11],
    wave: [-0.13, 0.12],
  },
  "Shippori Mincho": {
    punctuation: [0.62, -0.65],
    doubleQuoteOpen: [0.47, 0.54],
    doubleQuoteClose: [-0.49, -0.54],
    smallKana: [0.12, -0.13],
    cornerBracketOpen: [0.12, 0.11],
    cornerBracketClose: [0.12, 0.13],
    colon: [0.12, 0.12],
    bracketOpen: [0.10, 0.12],
    bracketClose: [0.11, 0.11],
    prolongedSound: [-0.16, 0.11],
    ellipsis: [-0.18, 0.11],
    dash: [-0.15, 0.11],
    wave: [-0.12, 0.11],
  },
  "Shippori Mincho B1": {
    punctuation: [0.62, -0.65],
    doubleQuoteOpen: [0.47, 0.55],
    doubleQuoteClose: [-0.49, -0.55],
    smallKana: [0.12, -0.13],
    cornerBracketOpen: [0.12, 0.11],
    cornerBracketClose: [0.12, 0.13],
    colon: [0.12, 0.12],
    bracketOpen: [0.10, 0.11],
    bracketClose: [0.10, 0.11],
    prolongedSound: [-0.16, 0.11],
    ellipsis: [-0.18, 0.11],
    dash: [-0.15, 0.11],
    wave: [-0.12, 0.11],
  },
  "Stick": {
    punctuation: [0.67, -0.69],
    doubleQuoteOpen: [0.54, 0.62],
    doubleQuoteClose: [-0.55, -0.63],
    smallKana: [0.07, -0.09],
    cornerBracketOpen: [0.11, 0.11],
    cornerBracketClose: [0.12, 0.11],
    colon: [0.17, 0.17],
    bracketOpen: [0.10, 0.11],
    bracketClose: [0.10, 0.12],
    prolongedSound: [-0.14, 0.15],
    ellipsis: [-0.19, 0.11],
    dash: [-0.15, 0.11],
    wave: [-0.12, 0.12],
  },
  "Yomogi": {
    punctuation: [0.66, -0.67],
    doubleQuoteOpen: [0.52, 0.57],
    doubleQuoteClose: [-0.52, -0.62],
    smallKana: [0.14, -0.23],
    cornerBracketOpen: [0.11, 0.04],
    cornerBracketClose: [0.14, 0.06],
    colon: [0.14, 0.14],
    bracketOpen: [0.12, 0.20],
    bracketClose: [0.13, 0.04],
    prolongedSound: [-0.14, 0.11],
    ellipsis: [-0.22, 0.11],
    dash: [-0.20, 0.13],
    wave: [-0.14, 0.14],
  },
  "Yuji Boku": {
    punctuation: [0.60, -0.59],
    doubleQuoteOpen: [0.48, 0.47],
    doubleQuoteClose: [-0.51, -0.52],
    smallKana: [0.12, -0.07],
    cornerBracketOpen: [0.14, 0.15],
    cornerBracketClose: [0.15, 0.10],
    colon: [0.17, 0.15],
    bracketOpen: [0.12, 0.16],
    bracketClose: [0.12, 0.08],
    prolongedSound: [-0.10, 0.10],
    ellipsis: [-0.21, 0.11],
    dash: [-0.14, 0.12],
    wave: [-0.13, 0.14],
  },
  "Yuji Mai": {
    punctuation: [0.60, -0.59],
    doubleQuoteOpen: [0.48, 0.47],
    doubleQuoteClose: [-0.51, -0.52],
    smallKana: [0.08, -0.07],
    cornerBracketOpen: [0.14, 0.15],
    cornerBracketClose: [0.15, 0.10],
    colon: [0.17, 0.15],
    bracketOpen: [0.12, 0.16],
    bracketClose: [0.12, 0.08],
    prolongedSound: [-0.13, 0.12],
    ellipsis: [-0.21, 0.11],
    dash: [-0.14, 0.12],
    wave: [-0.13, 0.14],
  },
  "Yuji Syuku": {
    punctuation: [0.60, -0.59],
    doubleQuoteOpen: [0.48, 0.47],
    doubleQuoteClose: [-0.51, -0.52],
    smallKana: [0.13, -0.08],
    cornerBracketOpen: [0.14, 0.15],
    cornerBracketClose: [0.15, 0.10],
    colon: [0.17, 0.15],
    bracketOpen: [0.12, 0.16],
    bracketClose: [0.12, 0.08],
    prolongedSound: [-0.12, 0.13],
    ellipsis: [-0.21, 0.11],
    dash: [-0.14, 0.12],
    wave: [-0.13, 0.14],
  },
  "Yusei Magic": {
    punctuation: [0.56, -0.57],
    doubleQuoteOpen: [0.43, 0.56],
    doubleQuoteClose: [-0.44, -0.54],
    smallKana: [0.10, -0.25],
    cornerBracketOpen: [0.11, 0.09],
    cornerBracketClose: [0.14, 0.12],
    colon: [0.21, 0.01],
    bracketOpen: [0.11, 0.08],
    bracketClose: [0.11, 0.12],
    prolongedSound: [-0.15, 0.08],
    ellipsis: [-0.28, 0.11],
    dash: [-0.17, 0.11],
    wave: [-0.12, 0.11],
  },
  "Zen Antique": {
    punctuation: [0.59, -0.61],
    doubleQuoteOpen: [0.52, 0.50],
    doubleQuoteClose: [-0.52, -0.52],
    smallKana: [0.10, -0.11],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.12, 0.12],
    colon: [0.18, 0.17],
    bracketOpen: [0.11, 0.12],
    bracketClose: [0.11, 0.11],
    prolongedSound: [-0.12, 0.12],
    ellipsis: [-0.48, 0.11],
    dash: [-0.39, 0.11],
    wave: [-0.12, 0.11],
  },
  "Zen Antique Soft": {
    punctuation: [0.59, -0.60],
    doubleQuoteOpen: [0.52, 0.51],
    doubleQuoteClose: [-0.52, -0.51],
    smallKana: [0.10, -0.10],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.12, 0.13],
    colon: [0.17, 0.18],
    bracketOpen: [0.11, 0.12],
    bracketClose: [0.11, 0.13],
    prolongedSound: [-0.12, 0.12],
    ellipsis: [-0.48, 0.12],
    dash: [-0.39, 0.12],
    wave: [-0.12, 0.12],
  },
  "Zen Kaku Gothic Antique": {
    punctuation: [0.61, -0.62],
    doubleQuoteOpen: [0.56, 0.54],
    doubleQuoteClose: [-0.55, -0.54],
    smallKana: [0.13, -0.13],
    cornerBracketOpen: [0.12, 0.11],
    cornerBracketClose: [0.13, 0.12],
    colon: [0.18, 0.15],
    bracketOpen: [0.11, 0.11],
    bracketClose: [0.11, 0.11],
    prolongedSound: [-0.12, 0.12],
    ellipsis: [-0.53, 0.07],
    dash: [-0.38, 0.11],
    wave: [-0.13, 0.12],
  },
  "Zen Kaku Gothic New": {
    punctuation: [0.61, -0.62],
    doubleQuoteOpen: [0.56, 0.54],
    doubleQuoteClose: [-0.55, -0.54],
    smallKana: [0.12, -0.11],
    cornerBracketOpen: [0.12, 0.11],
    cornerBracketClose: [0.13, 0.12],
    colon: [0.18, 0.15],
    bracketOpen: [0.11, 0.11],
    bracketClose: [0.11, 0.11],
    prolongedSound: [-0.12, 0.12],
    ellipsis: [-0.53, 0.07],
    dash: [-0.38, 0.11],
    wave: [-0.13, 0.12],
  },
  "Zen Kurenaido": {
    punctuation: [0.57, -0.58],
    doubleQuoteOpen: [0.51, 0.52],
    doubleQuoteClose: [-0.52, -0.52],
    smallKana: [0.09, -0.09],
    cornerBracketOpen: [0.14, 0.11],
    cornerBracketClose: [0.14, 0.17],
    colon: [0.18, 0.17],
    bracketOpen: [0.11, 0.11],
    bracketClose: [0.11, 0.15],
    prolongedSound: [-0.12, 0.12],
    ellipsis: [-0.52, 0.08],
    dash: [-0.39, 0.11],
    wave: [-0.14, 0.12],
  },
  "Zen Maru Gothic": {
    punctuation: [0.60, -0.61],
    doubleQuoteOpen: [0.56, 0.55],
    doubleQuoteClose: [-0.56, -0.53],
    smallKana: [0.12, -0.12],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.12, 0.12],
    colon: [0.18, 0.16],
    bracketOpen: [0.11, 0.12],
    bracketClose: [0.11, 0.13],
    prolongedSound: [-0.12, 0.15],
    ellipsis: [-0.53, 0.08],
    dash: [-0.39, 0.12],
    wave: [-0.13, 0.12],
  },
  "Zen Old Mincho": {
    punctuation: [0.58, -0.61],
    doubleQuoteOpen: [0.50, 0.52],
    doubleQuoteClose: [-0.52, -0.53],
    smallKana: [0.10, -0.11],
    cornerBracketOpen: [0.12, 0.12],
    cornerBracketClose: [0.11, 0.12],
    colon: [0.15, 0.15],
    bracketOpen: [0.10, 0.11],
    bracketClose: [0.10, 0.12],
    prolongedSound: [-0.16, 0.12],
    ellipsis: [-0.50, 0.11],
    dash: [-0.39, 0.11],
    wave: [-0.13, 0.12],
  },
  "Shizuru": {
    punctuation: [0.63, -0.51],
    doubleQuoteOpen: [0.32, 0.65],
    doubleQuoteClose: [-0.57, -0.65],
    smallKana: [0.20, -0.05],
    cornerBracketOpen: [0.05, 0.08],
    cornerBracketClose: [0.26, 0.06],
    colon: [0.26, 0.17],
    bracketOpen: [0.17, 0.31],
    bracketClose: [0.17, -0.02],
    prolongedSound: [-0.07, 0.19],
    ellipsis: [-0.75, 0.16],
    dash: [-0.23, 0.15],
    wave: [-0.13, 0.14],
  },
  "Cherry Bomb One": {
    punctuation: [0.39, -0.47],
    doubleQuoteOpen: [0.32, 0.65],
    doubleQuoteClose: [-0.57, -0.65],
    smallKana: [0.17, -0.27],
    cornerBracketOpen: [0.05, 0.22],
    cornerBracketClose: [0.22, 0.07],
    colon: [0.23, 0.18],
    bracketOpen: [0.16, 0.14],
    bracketClose: [0.16, 0.14],
    prolongedSound: [-0.15, 0.15],
    ellipsis: [-0.48, 0.14],
    dash: [-0.20, 0.14],
    wave: [-0.18, 0.14],
  },
  "Slackside One": {
    punctuation: [0.38, -0.64],
    doubleQuoteOpen: [0.24, 0.46],
    doubleQuoteClose: [-0.25, -0.60],
    smallKana: [0.06, 0.00],
    cornerBracketOpen: [0.10, 0.11],
    cornerBracketClose: [0.29, 0.15],
    colon: [0.26, 0.17],
    bracketOpen: [0.21, 0.23],
    bracketClose: [0.22, 0.06],
    prolongedSound: [-0.24, 0.15],
    ellipsis: [-0.39, 0.13],
    dash: [-0.28, 0.14],
    wave: [-0.21, 0.14],
  },
  "Darumadrop One": {
    punctuation: [0.35, -0.55],
    doubleQuoteOpen: [0.24, 0.50],
    doubleQuoteClose: [-0.08, -0.58],
    smallKana: [0.05, -0.13],
    cornerBracketOpen: [0.24, 0.11],
    cornerBracketClose: [0.16, 0.17],
    colon: [0.26, 0.17],
    bracketOpen: [0.20, 0.23],
    bracketClose: [0.19, 0.06],
    prolongedSound: [-0.21, 0.15],
    ellipsis: [-0.24, 0.13],
    dash: [-0.24, 0.14],
    wave: [-0.17, 0.14],
  },
};

// テーブルに無いフォント用の推定係数(gen.py が出力)。
// 文字ごとに [ax, bx, ay, by]: 縦書き字形のインク中心 = a + b * (横書き字形を回転・反転したときのインク中心)。
// 座標は「田」のインク中心からの相対、全角幅単位
export const verticalGlyphEstimator: Record<string, [number, number, number, number]> = {
  "、": [0.279, -0.089, -0.328, 0.000],
  "。": [0.271, -0.076, -0.240, -0.264],
  "〝": [0.139, -0.367, 0.255, -0.024],
  "〟": [-0.171, -0.290, -0.304, -0.081],
  "ぁ": [0.106, 0.865, -0.090, 0.748],
  "っ": [0.100, 0.317, -0.119, 0.910],
  "ゃ": [0.098, 0.141, -0.084, 0.685],
  "ァ": [0.106, 0.637, -0.051, 0.416],
  "ッ": [0.113, 1.057, -0.108, 0.904],
  "ョ": [0.120, 1.149, -0.110, 0.846],
  "「": [0.124, 0.981, 0.170, 0.625],
  "『": [0.118, 0.850, 0.131, 0.832],
  "：": [-0.001, 0.035, 0.014, 0.181],
  "；": [0.002, 0.042, 0.087, 0.273],
  "」": [0.095, 0.844, 0.023, 0.763],
  "』": [0.098, 0.868, 0.029, 0.765],
  "（": [0.022, 0.200, 0.175, 0.609],
  "【": [-0.010, -0.050, 0.139, 0.840],
  "〔": [0.006, 0.066, 0.168, 0.669],
  "＜": [0.101, 1.108, 0.012, 0.356],
  "｛": [0.002, 0.054, 0.156, 0.749],
  "）": [0.012, 0.118, -0.030, 0.660],
  "】": [0.012, 0.125, -0.020, 0.658],
  "〕": [0.024, 0.213, -0.091, 0.516],
  "＞": [0.108, 1.153, 0.090, 0.814],
  "｝": [-0.001, 0.023, 0.025, 0.783],
  "ー": [0.000, 0.000, 0.021, 0.298],
  "…": [0.000, 0.000, 0.081, 0.773],
  "―": [0.000, 0.000, 0.075, 0.760],
  "〰": [0.000, 0.000, 0.075, 0.672],
  "～": [0.000, 0.000, 0.036, 0.417],
  "〜": [0.000, 0.000, 0.015, 0.274],
};

// かぎ括弧の x は始め・終わりの対で推定する(gen.py が出力)。[a, b]: 始め括弧の x = a + b * (回転後の始め括弧 x - 終わり括弧 x) / 2、
// 終わり括弧はその符号反転。vert字形はデザインの高さに関わらず列の中心をはさんで対称に置かれるが、
// 回転した横書き字形は字形全体が低いフォント(Echion など)で両方とも左にずれるので、対の差だけを使ってそのずれを打ち消す
export const verticalBracketPairEstimator: Record<string, [number, number]> = {
  "「」": [0.001, 0.952],
  "『』": [0.005, 0.939],
};

// 推定時に各カテゴリの文字を描く変換(verticalText.ts の drawRotatedChar の呼び出しと同じ)
type GlyphTransform = { chars: string[]; angle: number; xflip: number; yflip: number; align: GlyphAlign };
const glyphTransforms: Record<keyof VerticalGlyphAdjust, GlyphTransform> = {
  punctuation:        { chars: ['、', '。'], angle: 0, xflip: 1, yflip: 1, align: "center" },
  doubleQuoteOpen:    { chars: ['〝'], angle: 0, xflip: -1, yflip: 1, align: "center" },
  doubleQuoteClose:   { chars: ['〟'], angle: 0, xflip: -1, yflip: 1, align: "center" },
  smallKana:          { chars: ['ぁ', 'っ', 'ゃ', 'ァ', 'ッ', 'ョ'], angle: 0, xflip: 1, yflip: 1, align: "center" },
  cornerBracketOpen:  { chars: ['「', '『'], angle: 90, xflip: 1, yflip: 1, align: "right" },
  cornerBracketClose: { chars: ['」', '』'], angle: 90, xflip: 1, yflip: 1, align: "left" },
  colon:              { chars: ['：', '；'], angle: 90, xflip: 1, yflip: 1, align: "center" },
  bracketOpen:        { chars: ['（', '【', '〔', '＜', '｛'], angle: 90, xflip: 1, yflip: 1, align: "right" },
  bracketClose:       { chars: ['）', '】', '〕', '＞', '｝'], angle: 90, xflip: 1, yflip: 1, align: "left" },
  prolongedSound:     { chars: ['ー'], angle: 90, xflip: 1, yflip: -1, align: "center" },
  ellipsis:           { chars: ['…'], angle: 90, xflip: 1, yflip: -1, align: "center" },
  dash:               { chars: ['―'], angle: 90, xflip: 1, yflip: -1, align: "center" }, // 〰 は持たないフォントが多く、代替フォントの寸法が混ざるので使わない
  wave:               { chars: ['～', '〜'], angle: 90, xflip: 1, yflip: -1, align: "center" },
};
const estimatorSampleText = '田' + Object.values(glyphTransforms).map(t => t.chars.join('')).join('');

// 推定結果のキャッシュ(サイズを除いた context.font と、実際に使われている字形の幅がキー)
const estimateCache = new Map<string, VerticalGlyphAdjust>();

export function getVerticalGlyphAdjust(context: CanvasRenderingContext2D): VerticalGlyphAdjust {
  const fontFamily = parseFontFamilyFromCssFont(context.font);
  if (fontFamily && verticalGlyphAdjustTable[fontFamily]) {
    return verticalGlyphAdjustTable[fontFamily];
  }

  // フォントの読み込み前は代替フォントの寸法になる。ローカルフォントでは document.fonts.check で読み込み前を
  // 判定しきれず、代替フォントで推定した値がキャッシュに残ったことがあるので、字形の幅をキーに含めて
  // 読み込み前後で別の推定値になるようにする
  const fontKey = context.font.replace(/\d+(?:\.\d+)?(?:px|pt|em|rem|%)(?:\s*\/\s*[^\s]+)?/, '');
  const unit = context.measureText('田').width;
  const key = `${fontKey}|${Math.round(context.measureText(estimatorSampleText).width / unit * 1000)}`;
  const cached = estimateCache.get(key);
  if (cached) { return cached; }

  const adjust = estimateVerticalGlyphAdjust(context);
  estimateCache.set(key, adjust);
  return adjust;
}

// 横書き字形の measureText の結果から、フォントの縦書き字形の位置に合わせるオフセットを推定する。
// context.font は対象フォントに設定済みであること
export function estimateVerticalGlyphAdjust(context: CanvasRenderingContext2D): VerticalGlyphAdjust {
  const unit = context.measureText('田').width; // 全角幅
  if (!(unit > 0)) { return defaultVerticalGlyphAdjust; }

  const [baseX, baseY] = inkCenter(context, '田', unit, glyphTransforms.punctuation /* 回転なし・中央寄せ */);
  // 回転・反転して描いたときのインク中心(「田」の中心からの相対、全角幅単位)
  const can: Record<string, [number, number]> = {};
  for (const t of Object.values(glyphTransforms)) {
    for (const c of t.chars) {
      const [x, y] = inkCenter(context, c, unit, t);
      can[c] = [(x - baseX) / unit, (y - baseY) / unit];
    }
  }
  // 縦書き字形のインク中心の推定値
  const target: Record<string, [number, number]> = {};
  for (const c of Object.keys(can)) {
    const [ax, bx, ay, by] = verticalGlyphEstimator[c];
    target[c] = [ax + bx * can[c][0], ay + by * can[c][1]];
  }
  for (const pair of Object.keys(verticalBracketPairEstimator)) {
    const [open, close] = [...pair];
    const [a, b] = verticalBracketPairEstimator[pair];
    const h = a + b * (can[open][0] - can[close][0]) * 0.5;
    target[open][0] = h;
    target[close][0] = -h;
  }

  const adjust = { ...defaultVerticalGlyphAdjust };
  for (const k of Object.keys(glyphTransforms) as (keyof VerticalGlyphAdjust)[]) {
    const chars = glyphTransforms[k].chars;
    let sx = 0, sy = 0;
    for (const c of chars) {
      sx += target[c][0] - can[c][0];
      sy += target[c][1] - can[c][1];
    }
    adjust[k] = [sx / chars.length, sy / chars.length];
  }
  return adjust;
}

// drawRotatedChar と同じ変換(オフセット0)で描いたときのインク中心(字送り枠の中心からの相対)
function inkCenter(context: CanvasRenderingContext2D, s: string, unit: number, t: GlyphTransform): [number, number] {
  const tm = context.measureText(s);
  const cw = tm.width;
  const originX = t.align === "right" ? unit * 0.5 - cw : t.align === "left" ? -unit * 0.5 : -cw * 0.5;
  const x = (originX + (tm.actualBoundingBoxRight - tm.actualBoundingBoxLeft) * 0.5) * t.xflip;
  const y = (unit * 0.5 + (tm.actualBoundingBoxDescent - tm.actualBoundingBoxAscent) * 0.5) * t.yflip;
  return t.angle === 90 ? [-y, x] : [x, y];
}

// CanvasRenderingContext2D.font の文字列から先頭のフォントファミリ名を取り出す
// 例: `normal 400 20px 'Noto Sans JP', sans-serif` → `Noto Sans JP`
export function parseFontFamilyFromCssFont(cssFont: string): string | null {
  const quoted = cssFont.match(/["']([^"']+)["']/);
  if (quoted) { return quoted[1]; }
  // サイズ指定(px等)の直後からカンマまでをファミリ名とみなす
  const m = cssFont.match(/\d+(?:\.\d+)?(?:px|pt|em|rem|%)(?:\s*\/\s*[^\s]+)?\s+([^,]+)/);
  if (m) { return m[1].trim(); }
  return null;
}
